/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// Types
import {
	TApiResponseForgotPassword,
	TBodyForgotPassword,
} from '../types';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const forgotPassword = (
	body: TBodyForgotPassword
): Promise<TApiResponseForgotPassword> => {
	return apiClient.post('/users/forgot_password', {
		...body,
	});
};

type TUseforgotPasswordOptions = {
	mutationConfig?: TMutationConfig<typeof forgotPassword>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useForgotPassword = ({
	mutationConfig,
}: TUseforgotPasswordOptions) => {
	const { t } = useTranslation();

	const { onSuccess } = mutationConfig || {};

	const { mutate, isPending: isLoading } = useMutation({
		mutationFn: forgotPassword,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.whoAmI.key],
			});

			onSuccess?.(...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading };
};
